import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FormPage from '../../components/ui/FormPage.tsx';
import { peekBackupCodes, takeBackupCodesOnce } from '../../services/mfa.ts';

const MfaBackupCodes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as any)?.from === 'setup';
  const [codes, setCodes] = useState<string[] | null>(null);

  useEffect(() => {
    const peeked = peekBackupCodes();
    setCodes(peeked);
  }, []);

  const title = useMemo(
    () => (fromSetup ? 'Códigos de recuperação' : 'Novos códigos de recuperação'),
    [fromSetup]
  );

  const download = () => {
    if (!codes?.length) return;
    const body = [
      'Meu Patrimônio — códigos de recuperação MFA',
      `Gerado em: ${new Date().toLocaleString('pt-BR')}`,
      '',
      ...codes,
      '',
      'Cada código só pode ser usado uma vez. Guarde em local seguro.',
    ].join('\n');
    const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meu-patrimonio-mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const continueNext = () => {
    takeBackupCodesOnce();
    navigate('/settings/security');
  };

  if (!codes?.length) {
    return (
      <FormPage
        backTo="/settings/security"
        backLabel="Voltar à segurança"
        breadcrumb="Config / Preferências / Segurança"
        title="Códigos de recuperação"
        subtitle="Os códigos já foram exibidos ou não estão disponíveis"
        footer={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/settings/security')}
          >
            Ir para segurança
          </button>
        }
      >
        <div className="warning-banner">
          <strong>Códigos indisponíveis</strong>
          Por segurança, os códigos de recuperação só são exibidos uma vez. Regenere-os em Segurança
          se precisar.
        </div>
      </FormPage>
    );
  }

  return (
    <FormPage
      backTo="/settings/security"
      backLabel="Voltar à segurança"
      breadcrumb="Config / Preferências / Segurança"
      title={title}
      subtitle="Guarde estes códigos — cada um só pode ser usado uma vez"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={download}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14" />
            </svg>
            Baixar
          </button>
          <button type="button" className="btn btn-primary" onClick={continueNext}>
            Copiei, continuar
          </button>
        </>
      }
    >
      {fromSetup && (
        <div className="mfa-steps" style={{ marginBottom: 18 }}>
          <div className="mfa-step done">
            <span className="mfa-step-num">✓</span> Escanear QR
          </div>
          <div className="mfa-step done">
            <span className="mfa-step-num">✓</span> Confirmar código
          </div>
          <div className="mfa-step active">
            <span className="mfa-step-num">3</span> Códigos de backup
          </div>
        </div>
      )}

      {fromSetup && (
        <div className="alert-banner green" style={{ marginBottom: 16 }}>
          <strong>MFA ativado com sucesso</strong>
          Sua conta agora exige verificação em duas etapas no login.
        </div>
      )}

      <div className="warning-banner">
        <strong>Estes códigos não serão exibidos novamente</strong>
        Baixe ou copie agora e guarde em local seguro. Use um código se perder acesso ao
        autenticador.
      </div>

      <div className="form-section-title">8 códigos de uso único</div>
      <div className="backup-grid">
        {codes.map(code => (
          <div key={code} className="backup-code">
            {code}
          </div>
        ))}
      </div>
    </FormPage>
  );
};

export default MfaBackupCodes;
